import type { Task } from '../api';

/**
 * What is holding this task up, said where the question is asked.
 *
 * The panel used to print "Blocked by an open task" while the blocker's title sat ~1400px further
 * down the same panel under LINKS, as inert text — the app knew the answer and would not say it.
 * That was fixed in the panel and then written again, generically, in the full view added
 * afterwards: the deeper surface told you less than the shallower one.
 *
 * So it lives here, once. Two surfaces showing one task should not be able to disagree about it,
 * and a third copy is a third chance to fix one and not the others.
 *
 * `blockedBy` can be empty while `isBlocked` is true — a blocker the reader cannot reach is still
 * a blocker — so the generic sentence is kept as the fallback rather than deleted.
 *
 * **"Blocked by", not "waiting on".** This said "Waiting on <task title>" until Phase 72, by which
 * point the same word also meant a promise from somebody outside the account (`WaitingOn.tsx`) and
 * work sitting on the shortlist. Three facts, one word, and the remedies are opposite: a blocker
 * clears when other work finishes, a promise clears when you chase a person. `isBlocked` and
 * `blockedBy` were already the schema's own nouns, so this is the copy catching up with them.
 *
 * **The full stop after the title is inside the button.** The panel is a 335px rail at 1280 and at
 * 375 alike, and a `<button>` is an atomic inline box whose shrink-to-fit width is
 * `min(max-content, available)` — so a blocker title longer than one line takes the whole line and
 * nothing can share it. Whatever followed the title therefore began a line of its own, and what
 * followed it was `. It clears itself when it is done.`: a bare fragment opening on a full stop, in
 * the sentence whose only job is to reassure. A 76-character title read
 * `Blocked by` / `Get the VLAN cutover signed off by the` / `network team and the security
 * reviewer` / `. It clears itself when it is done.`
 *
 * Letting the title flow as text would fix all of it and is not available. Chrome blockifies
 * `display: inline` on a form control straight back to `inline-block` — measured: the computed
 * value stays `inline-block` under `!important` — and `display: contents`, which does flow the
 * title into the sentence, leaves the button a 0×0 box, so its 3px focus outline has nothing to
 * paint. That trades a defect you can read for a keyboard focus you cannot see. Giving up the real
 * `<button>` for a `<span role="button">` buys the same flow for hand-rolled semantics, which the
 * comment at `Today.tsx:1763` already argues against.
 *
 * So the break stays and the *fragment* goes. With the stop carried inside the control the trailing
 * clause opens on "It", and every line of the banner begins with a word — which is what the reader
 * was reacting to. `e2e/blockerNamed.e2e.spec.ts` measures that line by line.
 */
export function BlockedBanner({
  task,
  onOpenTask,
}: {
  task: Task;
  onOpenTask?: (id: string) => void;
}) {
  if (!task.isBlocked) return null;

  /*
    A test id, because `.banner` is not enough to name this one. `WaitingBanner` renders a
    `<p class="banner is-notice">` inside its own wrapper, so on a task that is both blocked and
    waiting — the case this vocabulary split exists for — `.banner` resolves to two elements and a
    strict locator fails. The class is shared styling; the id is identity.
  */
  return (
    <p className="banner" data-testid="blocked-banner">
      {task.blockedBy.length === 0 ? (
        <>Blocked by an open task. It clears itself when the blocker is done.</>
      ) : (
        <>
          Blocked by{' '}
          {task.blockedBy.map((b, i) => {
            const last = i === task.blockedBy.length - 1;
            /*
              The sentence's full stop, carried by the last title rather than left behind it — see
              the note above. Skipped when the title already ends a sentence of its own, because
              "Ship it?." is a different kind of wrong from the one being fixed — a case nothing
              exercised until `blockerNamed.e2e.spec.ts` grew a blocker titled "Ship it?".
            */
            const stop = last && !/[.!?]$/.test(b.title);
            /*
              And the stop is hidden from the accessible name, while staying inside the box.

              The control has no `aria-label`, so its name is its text — which with the stop
              appended was "…and the security reviewer." and announced as "…and the security
              reviewer. button". A full stop is punctuation of the *sentence*, not part of what the
              control is called, and a reader listening has no line breaks to be bothered by, which
              is the only problem the stop was moved here to solve.

              `aria-hidden` on a span rather than the stop moving back outside the button: it has
              to stay within the atomic inline box or the whole mechanism goes with it. A button's
              shrink-to-fit width is `min(max-content, available)`, so a long title takes the line
              and nothing can share it — leaving the stop outside is what put a bare "." at the
              start of the last line, which is the defect. Inside an ignored span it still cannot
              wrap away from the title.

              `blockerNamed.e2e.spec.ts` holds both halves: the 375 wrap case still asserts no
              rendered line begins with punctuation, and a new case reads the button's accessible
              name and requires the stop to be absent from it.
            */
            return (
              <span key={b.id}>
                {i > 0 && (last ? ' and ' : ', ')}
                {onOpenTask ? (
                  <button className="link" onClick={() => onOpenTask(b.id)}>
                    {b.title}
                    {stop && <span aria-hidden="true">.</span>}
                  </button>
                ) : (
                  <>
                    {b.title}
                    {stop && '.'}
                  </>
                )}
              </span>
            );
          })}{' '}
          It clears itself when {task.blockedBy.length === 1 ? 'it is' : 'they are'} done.
        </>
      )}
    </p>
  );
}
